/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { primitiveColorTokensV2 } from '@uber/base-specs/src/generated/base-color-tokens-v2';
import { hexToRgb } from '../styles';
import { HIERARCHY, KIND } from './constants';
import type { Hierarchy, Kind } from './types';
import type { ThemeMode } from '../helpers/types';

/**
 * Alpha applied to every `banner-button-background-subtle` swatch below,
 * per the Base 2.0 Banner color spec.
 */
const V2_ACTION_BACKGROUND_SUBTLE_ALPHA = '0.7';
type V2ActionBackgroundSubtleSwatches = {
  light: string;
  dark: string;
};

/**
 * `banner-button-background-subtle` primitive swatches, keyed by `kind`
 * (used for `HIERARCHY.low`) — each rendered at 70% alpha.
 */
const V2_ACTION_BACKGROUND_SUBTLE: Record<Kind, V2ActionBackgroundSubtleSwatches> = {
  [KIND.info]: {
    light: primitiveColorTokensV2.blue96,
    dark: primitiveColorTokensV2.blue35
  },
  [KIND.negative]: {
    light: primitiveColorTokensV2.red91,
    dark: primitiveColorTokensV2.red35
  },
  [KIND.positive]: {
    light: primitiveColorTokensV2.green96,
    dark: primitiveColorTokensV2.green40
  },
  [KIND.warning]: {
    light: primitiveColorTokensV2.orange91,
    dark: primitiveColorTokensV2.orange40
  }
};
type V2ActionBackgroundBoldOverlay = {
  light: string;
  dark: string;
};
const BLACK_20 = 'rgba(0, 0, 0, 0.2)';
const WHITE_15 = 'rgba(255, 255, 255, 0.15)';
const WHITE_30 = 'rgba(255, 255, 255, 0.3)';

/**
 * `banner-button-background-bold` overlays, keyed by `kind` (used for
 * `HIERARCHY.high`). Unlike the subtle swatches above, bold is a
 * translucent black/white overlay painted on top of the (already-saturated)
 * bold root background, rather than a distinct primitive.
 */
const V2_ACTION_BACKGROUND_BOLD: Record<Kind, V2ActionBackgroundBoldOverlay> = {
  [KIND.info]: {
    light: BLACK_20,
    dark: WHITE_15
  },
  [KIND.negative]: {
    light: BLACK_20,
    dark: WHITE_15
  },
  [KIND.positive]: {
    light: BLACK_20,
    dark: WHITE_30
  },
  [KIND.warning]: {
    light: WHITE_30,
    dark: WHITE_30
  }
};

/**
 * Resolves Banner's action (button) background color from the Base 2.0
 * color spec. Only call this when Base 2.0 colors are active
 * (`useBase2Features().colors`); v1 themes keep using the existing
 * `bannerAction{Low,High}{Info,Negative,Positive,Warning}` component color
 * tokens instead.
 */
export function getV2ButtonBackgroundColor(kind: Kind, hierarchy: Hierarchy, themeMode: ThemeMode | undefined): string {
  const isDark = themeMode === 'dark';
  if (hierarchy === HIERARCHY.high) {
    const overlay = V2_ACTION_BACKGROUND_BOLD[kind] ?? V2_ACTION_BACKGROUND_BOLD[KIND.info];
    return isDark ? overlay.dark : overlay.light;
  }
  const swatches = V2_ACTION_BACKGROUND_SUBTLE[kind] ?? V2_ACTION_BACKGROUND_SUBTLE[KIND.info];
  const hex = isDark ? swatches.dark : swatches.light;
  return hexToRgb(hex, V2_ACTION_BACKGROUND_SUBTLE_ALPHA) ?? hex;
}